/** One update block: an optional flat conditions builder (ALL/ANY + per-row
 *  NOT chips, mirroring the notification RulesEditor) above an ordered list of
 *  mutations (path picked from the catalog, operator from the entry's
 *  vocabulary, typed value editor).
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import type { BatchEditCatalog, BatchEditPath } from '@/api/batchEdit';
import { CONDITION_OPERATOR_LABELS, MUTATION_OP_LABELS, PRESENCE_OPERATORS } from '@/api/batchEdit';

import {
  emptyCondition,
  emptyOp,
  entryByPath,
  type BlockDraft,
  type ConditionDraft,
  type OpDraft,
} from './model';
import { OpValueEditor, TypedValueEditor } from './ValueEditor';

interface Props {
  index: number;
  value: BlockDraft;
  paths: BatchEditPath[];
  catalog: BatchEditCatalog;
  canRemove: boolean;
  onChange: (patch: Partial<BlockDraft>) => void;
  onRemove: () => void;
}

export function BlockEditor({ index, value, paths, catalog, canRemove, onChange, onRemove }: Props) {
  const patchCondition = (id: string, patch: Partial<ConditionDraft>) =>
    onChange({ conditions: value.conditions.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  const patchOp = (id: string, patch: Partial<OpDraft>) =>
    onChange({ ops: value.ops.map((o) => (o.id === id ? { ...o, ...patch } : o)) });

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Update block {index + 1}
        </Typography>
        {canRemove && (
          <IconButton size="small" aria-label="Remove update block" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Stack spacing={2}>
        {/* ---- Conditions ---- */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
              Only apply when{value.conditions.length > 1 ? '…' : ' (optional)…'}
            </Typography>
            {value.conditions.length > 1 && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={value.combinator}
                onChange={(_, v: 'AND' | 'OR' | null) => v && onChange({ combinator: v })}
              >
                <ToggleButton value="AND">Match ALL</ToggleButton>
                <ToggleButton value="OR">Match ANY</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Stack>
          <Stack spacing={1}>
            {value.conditions.map((c) => (
              <ConditionRow
                key={c.id}
                value={c}
                paths={paths}
                operators={catalog.condition_operators}
                onChange={(patch) => patchCondition(c.id, patch)}
                onRemove={() =>
                  onChange({ conditions: value.conditions.filter((x) => x.id !== c.id) })
                }
              />
            ))}
          </Stack>
          <Box sx={{ mt: value.conditions.length ? 1 : 0 }}>
            <Chip
              size="small"
              variant="outlined"
              icon={<AddIcon />}
              label="Add condition"
              onClick={() => onChange({ conditions: [...value.conditions, emptyCondition()] })}
            />
          </Box>
        </Box>

        {/* ---- Mutations ---- */}
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            …change:
          </Typography>
          <Stack spacing={1.5}>
            {value.ops.map((o) => (
              <OpRow
                key={o.id}
                value={o}
                paths={paths}
                canRemove={value.ops.length > 1}
                onChange={(patch) => patchOp(o.id, patch)}
                onRemove={() => onChange({ ops: value.ops.filter((x) => x.id !== o.id) })}
              />
            ))}
          </Stack>
          <Box sx={{ mt: 1 }}>
            <Chip
              size="small"
              variant="outlined"
              icon={<AddIcon />}
              label="Add field change"
              onClick={() => onChange({ ops: [...value.ops, emptyOp()] })}
            />
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// One condition row: [NOT] [field] [operator] [value]
// ---------------------------------------------------------------------------

interface ConditionRowProps {
  value: ConditionDraft;
  paths: BatchEditPath[];
  operators: string[];
  onChange: (patch: Partial<ConditionDraft>) => void;
  onRemove: () => void;
}

function ConditionRow({ value, paths, operators, onChange, onRemove }: ConditionRowProps) {
  const entry = entryByPath(paths, value.path.trim());
  const presence = (PRESENCE_OPERATORS as readonly string[]).includes(value.op);

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Tooltip title={value.not ? 'Negated — click to un-negate' : 'Click to negate this condition'}>
        <Chip
          size="small"
          label="NOT"
          color={value.not ? 'warning' : 'default'}
          variant={value.not ? 'filled' : 'outlined'}
          onClick={() => onChange({ not: !value.not })}
          sx={{ mt: 0.75 }}
        />
      </Tooltip>
      <Autocomplete
        freeSolo
        size="small"
        sx={{ flex: 2 }}
        options={paths.map((p) => p.path)}
        value={value.path}
        onInputChange={(_, text) => onChange({ path: text, value: '' })}
        renderOption={(props, option) => {
          const optEntry = entryByPath(paths, option);
          return (
            <li {...props} key={option}>
              <Stack>
                <Typography variant="body2">{optEntry?.label ?? option}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option}
                  {optEntry ? ` · ${optEntry.type}` : ''}
                </Typography>
              </Stack>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Field"
            placeholder="cta_button_behavior"
            helperText={entry ? entry.label : value.path ? 'Free-form path (read-only check)' : undefined}
          />
        )}
      />
      <TextField
        select
        label="Operator"
        size="small"
        sx={{ flex: 1.4, minWidth: 150 }}
        value={value.op}
        onChange={(e) => onChange({ op: e.target.value })}
      >
        {operators.map((op) => (
          <MenuItem key={op} value={op}>
            {CONDITION_OPERATOR_LABELS[op] ?? op}
          </MenuItem>
        ))}
      </TextField>
      {!presence && (
        <Box sx={{ flex: 1.6 }}>
          <TypedValueEditor entry={entry} value={value.value} onChange={(v) => onChange({ value: v })} />
        </Box>
      )}
      <IconButton size="small" aria-label="Remove condition" onClick={onRemove} sx={{ mt: 0.5 }}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// One mutation row: [field] [operator] [value editor]
// ---------------------------------------------------------------------------

interface OpRowProps {
  value: OpDraft;
  paths: BatchEditPath[];
  canRemove: boolean;
  onChange: (patch: Partial<OpDraft>) => void;
  onRemove: () => void;
}

function OpRow({ value, paths, canRemove, onChange, onRemove }: OpRowProps) {
  const entry = entryByPath(paths, value.path);
  const ops = entry?.ops ?? ['set'];

  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Autocomplete
            size="small"
            sx={{ flex: 2 }}
            options={paths}
            getOptionLabel={(p) => (typeof p === 'string' ? p : p.label)}
            isOptionEqualToValue={(a, b) => a.path === b.path}
            value={entry}
            onChange={(_, picked) => {
              const nextOps = picked?.ops ?? ['set'];
              onChange({
                path: picked?.path ?? '',
                op: nextOps.includes(value.op) ? value.op : (nextOps[0] ?? 'set'),
                value: '',
              });
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.path}>
                <Stack>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.path} · {option.type}
                    {option.nullable ? ' · nullable' : ''}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Field to change" helperText={entry?.help || undefined} />
            )}
          />
          <TextField
            select
            label="Operation"
            size="small"
            sx={{ flex: 1, minWidth: 150 }}
            value={ops.includes(value.op) ? value.op : (ops[0] ?? 'set')}
            onChange={(e) => onChange({ op: e.target.value, value: '' })}
            disabled={!entry}
          >
            {ops.map((op) => (
              <MenuItem key={op} value={op}>
                {MUTATION_OP_LABELS[op] ?? op}
              </MenuItem>
            ))}
          </TextField>
          {canRemove && (
            <IconButton size="small" aria-label="Remove field change" onClick={onRemove} sx={{ mt: 0.5 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <OpValueEditor entry={entry} draft={value} onChange={onChange} />
      </Stack>
    </Paper>
  );
}
